'use client';

import { useEffect, useId, useState } from 'react';
import { ArrowUpRight, Music, Play, X } from 'lucide-react';
import { Button } from '@/atoms/Button/Button';
import { Container } from '@/atoms/Container/Container';
import { Iframe } from '@/atoms/Iframe/Iframe';
import { Image } from '@/atoms/Image/Image';
import { Typography } from '@/atoms/Typography/Typography';
import { useOgMetadata } from '@/hooks/useOgMetadata/useOgMetadata';
import { VIDEO_EMBED_PROPS } from '../Provider.constants';
import { parseSpotifyUrl } from './spotifyUrl';

/** Fired when any card starts a provider player, so other cards close theirs (one active player). */
export const EMBED_PLAYER_OPENED_EVENT = 'pubky:embed-player-opened';

const KIND_LABEL: Record<string, string> = {
  track: 'Song',
  album: 'Album',
  artist: 'Artist',
  playlist: 'Playlist',
};

/** Spotify's documented player heights: compact for a track, full list for the other kinds. */
const playerHeight = (kind: string) => (kind === 'track' ? 152 : 352);

/** Seconds after which an unresponsive frame shows the Open in Spotify fallback. */
const LOAD_TIMEOUT_MS = 12000;

type PlayerState = 'idle' | 'loading' | 'ready' | 'slow';

/**
 * Pubky-owned resource card for a Spotify link.
 *
 * Before intent the card shows only Pubky chrome (type, source, title and artwork from the app's own
 * metadata endpoint, as every link preview does). No Spotify frame or script exists until the reader
 * presses Play. Play mounts
 * Spotify's own visible player; it may still need a press inside that player (browser autoplay
 * rules), so the card never claims playback has started. Close removes the frame. Starting a player
 * on another card closes this one, so an old frame can never keep playing underneath.
 *
 * Callers key this component by canonical URL so player state resets when the resource changes.
 */
export function SpotifyResourceCard({ url }: { url: string }) {
  const resource = parseSpotifyUrl(url);
  const cardId = useId();
  const [state, setState] = useState<PlayerState>('idle');
  const { metadata, isLoading } = useOgMetadata(resource?.href ?? null);

  useEffect(() => {
    if (state === 'idle') return;
    const onOtherOpened = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== cardId) setState('idle');
    };
    window.addEventListener(EMBED_PLAYER_OPENED_EVENT, onOtherOpened);
    return () => window.removeEventListener(EMBED_PLAYER_OPENED_EVENT, onOtherOpened);
  }, [state, cardId]);

  useEffect(() => {
    if (state !== 'loading') return;
    const timer = setTimeout(() => setState('slow'), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [state]);

  if (!resource) return null;

  const kindLabel = KIND_LABEL[resource.kind] ?? 'Music';
  const title = metadata?.title?.replace(/\s*\|\s*Spotify$/i, '') || `Spotify ${kindLabel.toLowerCase()}`;
  const open = state !== 'idle';

  const play = () => {
    setState('loading');
    window.dispatchEvent(new CustomEvent(EMBED_PLAYER_OPENED_EVENT, { detail: cardId }));
  };

  return (
    <Container
      data-testid="spotify-resource-card"
      data-player-state={state}
      className="w-full gap-3 rounded-md border border-border bg-card p-4"
      aria-label={`${kindLabel} on Spotify: ${title}`}
      role="group"
    >
      <Container className="flex-row items-center justify-between gap-3">
        <Typography size="sm" className="font-medium text-muted-foreground">
          {kindLabel}
        </Typography>
        <Container className="flex-row items-center gap-1">
          <a
            href={resource.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="spotify-source-link"
            className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <Music size={14} aria-hidden="true" />
            Spotify
            <ArrowUpRight size={14} aria-hidden="true" />
          </a>
          {open && (
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Close Spotify player"
              data-testid="spotify-close"
              onClick={() => setState('idle')}
            >
              <X />
            </Button>
          )}
        </Container>
      </Container>

      {!open && (
        <Container className="flex-row items-center gap-4">
          {metadata?.image ? (
            <Image
              src={metadata.image}
              alt=""
              width={80}
              height={80}
              className="size-20 shrink-0 rounded-md object-cover"
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div
              className={`flex size-20 shrink-0 items-center justify-center rounded-md bg-muted ${isLoading ? 'animate-pulse' : ''}`}
            >
              <Music className="text-muted-foreground" aria-hidden="true" />
            </div>
          )}
          <Container className="min-w-0 gap-1">
            <a href={resource.href} target="_blank" rel="noopener noreferrer" className="hover:underline">
              <Typography size="lg" className="font-semibold wrap-break-word">
                {title}
              </Typography>
            </a>
          </Container>
        </Container>
      )}

      {open && (
        <Iframe
          title={`Spotify ${resource.kind} player: ${title}`}
          src={resource.src}
          height={String(playerHeight(resource.kind))}
          allow={VIDEO_EMBED_PROPS.allow}
          sandbox={`${VIDEO_EMBED_PROPS.sandbox} allow-popups-to-escape-sandbox`}
          referrerPolicy="no-referrer"
          loading="eager"
          data-testid="spotify-player-frame"
          className="block w-full border-0"
          onLoad={() => setState((s) => (s === 'loading' || s === 'slow' ? 'ready' : s))}
        />
      )}

      {!open && (
        <Container className="flex-row items-center gap-3">
          <Button
            variant="brand"
            size="sm"
            onClick={play}
            data-testid="spotify-play"
            aria-label={`Play ${title} on Spotify`}
          >
            <Play aria-hidden="true" />
            Play
          </Button>
        </Container>
      )}

      <Typography size="sm" className="text-muted-foreground" role="status" data-testid="spotify-status">
        {state === 'idle' && 'Play opens the Spotify player here.'}
        {state === 'loading' && 'Loading Spotify…'}
        {state === 'ready' && 'If it has not started, press play in the Spotify player. Playback may be a preview.'}
        {state === 'slow' && (
          <>
            Spotify is not responding.{' '}
            <a href={resource.href} target="_blank" rel="noopener noreferrer" className="underline">
              Open in Spotify
            </a>
          </>
        )}
      </Typography>
    </Container>
  );
}
